import React, { useState, useEffect } from 'react';
import { Brain, Clock, Zap, DollarSign, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const ThinkProgressModal = ({ isOpen, progress, onClose }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);

  // Real-time timer effect
  useEffect(() => {
    let interval;
    
    if (isOpen && progress?.status && progress.status !== 'completed' && progress.status !== 'error') {
      if (!startTime) {
        setStartTime(Date.now());
      }
      
      interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 500); // Update every 500ms for smoother progress
    } else if (progress?.status === 'completed' || progress?.status === 'error') {
      setStartTime(null);
      setElapsedTime(0);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isOpen, progress?.status, startTime]);

  // Reset timer when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartTime(Date.now());
      setElapsedTime(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'starting':
        return <Loader className="w-6 h-6 animate-spin text-blue-500" />;
      case 'connecting':
        return <Loader className="w-6 h-6 animate-spin text-blue-500" />;
      case 'connected':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'sending':
        return <Zap className="w-6 h-6 text-yellow-500" />;
      case 'parsing':
        return <Brain className="w-6 h-6 text-purple-500" />;
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Loader className="w-6 h-6 animate-spin text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'starting':
        return 'text-blue-600';
      case 'connecting':
        return 'text-blue-600';
      case 'connected':
        return 'text-green-600';
      case 'sending':
        return 'text-yellow-600';
      case 'parsing':
        return 'text-purple-600';
      case 'completed':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else if (seconds > 0) {
      return `${seconds}s`;
    } else {
      return '0s';
    }
  };

  // Use real-time elapsed time or fallback to progress time
  const getDisplayTime = () => {
    if (progress?.status === 'completed' || progress?.status === 'error') {
      return progress?.timeElapsed || 0;
    }
    return elapsedTime || progress?.timeElapsed || 0;
  };

  const formatCost = (cost) => {
    if (!cost) return '¥0.0000';
    return `¥${cost.totalCost.toFixed(4)}`;
  };

  const formatTokens = (tokens) => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return tokens.toString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">AI Analysis Progress</h3>
              <p className="text-sm text-gray-600">DeepSeek AI is thinking about your problem</p>
            </div>
          </div>
                     {(progress?.status === 'completed' || progress?.status === 'error') && (
             <button
               onClick={onClose}
               className="text-gray-400 hover:text-gray-600 transition-colors"
               title="Close modal"
             >
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
               </svg>
             </button>
           )}
        </div>

        {/* Progress Status */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            {getStatusIcon(progress?.status)}
            <div className="flex-1">
              <h4 className={`font-semibold ${getStatusColor(progress?.status)}`}>
                {progress?.message || 'Initializing...'}
              </h4>
                             {progress?.error && (
                 <p className="text-sm text-red-600 mt-1">{progress.error}</p>
               )}
                              {progress?.status === 'sending' && getDisplayTime() > 15000 && (
                 <p className="text-sm text-yellow-600 mt-1">
                   This is taking longer than usual. Please be patient...
                 </p>
               )}
               {progress?.status === 'sending' && getDisplayTime() > 30000 && (
                  <p className="text-sm text-orange-600 mt-1">
                    Still processing... This may take a moment for complex problems.
                  </p>
                )}
                        {progress?.status === 'sending' && getDisplayTime() > 60000 && (
            <p className="text-sm text-red-600 mt-1">
              Processing very complex problem... This may take up to 180 seconds.
            </p>
          )}
         {progress?.status === 'sending' && getDisplayTime() > 90000 && (
            <p className="text-sm text-red-600 mt-1">
              Processing extremely complex problem... This may take up to 180 seconds.
            </p>
          )}
               {progress?.status === 'sending' && getDisplayTime() > 45000 && (
                  <p className="text-sm text-red-600 mt-1">
                    Processing complex problem... This may take up to 120 seconds.
                  </p>
                )}
                {progress?.status === 'connecting' && (
                  <p className="text-sm text-blue-600 mt-1">
                    Verifying DeepSeek API connectivity...
                  </p>
                )}
                {progress?.status === 'connected' && (
                  <p className="text-sm text-green-600 mt-1">
                    ✓ DeepSeek API connection established successfully
                  </p>
                )}
            </div>
          </div>
          
                     {/* Progress Bar */}
           <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
             <div 
               className={`h-2 rounded-full transition-all duration-500 ${
                 progress?.status === 'completed' ? 'bg-green-500' :
                 progress?.status === 'error' ? 'bg-red-500' :
                 'bg-gradient-to-r from-indigo-500 to-purple-500'
               }`}
               style={{ 
                 width: progress?.status === 'completed' ? '100%' :
                        progress?.status === 'error' ? '100%' :
                        progress?.status === 'parsing' ? '85%' :
                        progress?.status === 'sending' ? Math.min(45 + (getDisplayTime() / 1000) * 2, 85) + '%' :
                        progress?.status === 'connected' ? '25%' :
                        progress?.status === 'connecting' ? '15%' : '10%'
               }}
             />
           </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 gap-4 mb-6">
          {/* Time Elapsed */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Time Elapsed</span>
            </div>
            <span className="text-lg font-semibold text-blue-900">
              {formatTime(getDisplayTime())}
              {progress?.status === 'sending' && (
                <span className="ml-1 text-blue-400 animate-pulse">●</span>
              )}
            </span>
          </div>

          {/* Tokens Used */}
          <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">Tokens Used</span>
            </div>
            <span className="text-lg font-semibold text-purple-900">
              {formatTokens(progress?.tokensUsed || Math.floor(getDisplayTime() / 1000 * 50))}
              {progress?.status === 'sending' && (
                <span className="ml-1 text-purple-400 animate-pulse">●</span>
              )}
            </span>
          </div>

          {/* Cost Incurred */}
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">Cost Incurred</span>
            </div>
            <div className="text-right">
              <span className="text-lg font-semibold text-green-900">
                {formatCost(progress?.cost || { 
                  totalCost: Math.floor(getDisplayTime() / 1000 * 50) * 0.000002 
                })}
                {progress?.status === 'sending' && (
                  <span className="ml-1 text-green-400 animate-pulse">●</span>
                )}
              </span>
              {progress?.cost?.isDiscountPeriod && (
                <div className="text-xs text-green-600">Discount Period</div>
              )}
            </div>
          </div>
        </div>

        {/* Template Structure Info */}
        {progress?.details && (
          <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-900">Analysis Details</span>
            </div>
            <p className="text-sm text-indigo-700">{progress.details}</p>
            {progress?.status === 'connected' && (
              <div className="mt-2 pt-2 border-t border-indigo-200">
                <div className="flex items-center gap-2 text-xs text-green-700">
                  <CheckCircle className="w-3 h-3" />
                  <span>DeepSeek API connection verified and ready for analysis</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Detailed Cost Breakdown */}
        {progress?.cost && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h5 className="text-sm font-semibold text-gray-900 mb-3">Cost Breakdown</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Input Tokens:</span>
                <span className="font-medium">
                  ¥{progress.cost.inputCost.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Output Tokens:</span>
                <span className="font-medium">
                  ¥{progress.cost.outputCost.toFixed(4)}
                </span>
              </div>
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span className="text-gray-900">Total Cost:</span>
                <span className="text-gray-900">
                  ¥{progress.cost.totalCost.toFixed(4)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {progress?.status === 'completed' ? (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors"
            >
              View Results
            </button>
          ) : progress?.status === 'error' ? (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Close
            </button>
          ) : (
            <div className="flex-1 px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-center">
              Please wait...
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Powered by DeepSeek AI • Real-time analysis
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThinkProgressModal; 